import { Pressable, Text, TextInput, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  blankDropsetStep,
  MAX_DROPSET_STEPS,
  type DropsetDraft,
} from '@heraion/shared';
import type { useTheme } from './theme';
import { makeStyles } from './loggerStyles';
import { useFieldFocusReporter } from './useFieldFocus';
import { useTransientTip } from './useTransientTip';
import { stateAria } from "../a11y";

// Dropset chain attached to a parent set. Surfaces variant choice +
// per-step reps/weight inputs + add/remove. Born from the legacy web's
// `renderDropsetRow` (`7ae8e35e:apps/web/src/workout/WorkoutLog.tsx:1603-1719`);
// each step parses to a canonical Dropset entry at save via
// buildPayload's `builtDropset` branch.
export function DropsetBlock({
  dropset,
  isBodyweight,
  inputsLocked,
  addLocked = false,
  onChange,
  theme,
}: {
  dropset: DropsetDraft;
  isBodyweight: boolean;
  inputsLocked: boolean;
  /** Editing a past session (edit-scope §9): existing step VALUES stay
   *  editable, but ADDING a new step is forbidden — the + greys + balloons.
   *  Default false so the card logger's callers are unaffected. */
  addLocked?: boolean;
  onChange: (next: DropsetDraft | null) => void;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const { showTip, tipNode } = useTransientTip(theme);
  // On a dropset set these ARE the numeric entry: FocusPanelInputs hides its
  // own reps/weight row and points here, and FocusPanelDetails never locks this
  // block. So the rest-end resume has to wait for these two like any other cell
  // (TM-18a) — the surface a review found after the cardio one.
  const fieldFocus = useFieldFocusReporter();
  const addLockMsg = t('mobile.timeline.addStepLocked', {
    defaultValue: "Editing a past session: you can't add a dropset step.",
  });
  const updateStep = (idx: number, patch: { reps?: string; weight?: string }) =>
    onChange({
      ...dropset,
      steps: dropset.steps.map((step, j) =>
        j === idx ? { ...step, ...patch } : step,
      ),
    });
  const removeStep = (idx: number) =>
    onChange({
      ...dropset,
      steps: dropset.steps.filter((_, j) => j !== idx),
    });
  const addStep = () =>
    onChange({ ...dropset, steps: [...dropset.steps, blankDropsetStep()] });
  // Slice 4.3 — DropsetBlock renders ONLY the per-step rows. The
  // variant chips + ? hint + × remove control bar moved up into
  // SetRow (rendered just above this block via the setTypeBar
  // condition) so the bar can also be surfaced from the ⋯ menu's
  // "Set type" entry on a plain working set.
  return (
    <View style={styles.dropsetBlock}>
      {tipNode}
      {dropset.steps.map((step, idx) => {
        const weightPlaceholder = isBodyweight
          ? t('workout.extra')
          : dropset.variant === 'myorep'
            ? t('workout.dropsetSameLoad')
            : t('workout.weight');
        const canAdd = dropset.steps.length < MAX_DROPSET_STEPS;
        return (
          <View key={idx} style={styles.dropsetStepRow}>
            {/* Slice 4.4 — per-step pill removed. Antenore's flow is
                "prepare drops, lift them, mark the whole set done via
                the parent pill". The per-step completedAt field stays
                on the schema for back-compat with rows persisted
                while the per-step pill shipped. */}
            <Text style={styles.dropsetStepIndex}>↳ {idx + 1}</Text>
            <TextInput
              style={[
                styles.dropsetStepInput,
                inputsLocked && styles.setInputLocked,
              ]}
              keyboardType="number-pad"
              inputMode="numeric"
              placeholder={t('workout.reps')}
              placeholderTextColor={theme.palette.ink3}
              value={step.reps}
              onChangeText={(reps) => updateStep(idx, { reps })}
              onFocus={() => fieldFocus.onFocus(`dropset.${idx}.reps`)}
              onBlur={() => fieldFocus.onBlur(`dropset.${idx}.reps`)}
              maxLength={4}
              editable={!inputsLocked}
            />
            <TextInput
              style={[
                styles.dropsetStepInput,
                inputsLocked && styles.setInputLocked,
              ]}
              keyboardType="decimal-pad"
              inputMode="decimal"
              placeholder={weightPlaceholder}
              placeholderTextColor={theme.palette.ink3}
              value={step.weight}
              onChangeText={(weight) => updateStep(idx, { weight })}
              onFocus={() => fieldFocus.onFocus(`dropset.${idx}.weight`)}
              onBlur={() => fieldFocus.onBlur(`dropset.${idx}.weight`)}
              maxLength={6}
              editable={!inputsLocked}
            />
            {canAdd ? (
              <Pressable
                accessibilityRole="button"
                accessibilityState={{ disabled: addLocked }}
                {...stateAria({ disabled: addLocked })}
                accessibilityLabel={t('workout.dropsetAddStep')}
                onPress={addLocked ? () => showTip(addLockMsg) : addStep}
                disabled={inputsLocked}
                hitSlop={8}
                style={({ pressed }) => [
                  styles.dropsetStepAdd,
                  addLocked && { opacity: 0.4 },
                  pressed && !addLocked && styles.iconButtonPressed,
                ]}
              >
                <Text style={styles.dropsetStepAddText}>+</Text>
              </Pressable>
            ) : (
              <View style={styles.dropsetStepAdd} />
            )}
            {dropset.steps.length > 1 ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t('workout.dropsetRemoveStep')}
                onPress={() => removeStep(idx)}
                disabled={inputsLocked}
                hitSlop={8}
                style={({ pressed }) => [
                  styles.dropsetStepRemove,
                  pressed && styles.dropsetStepRemovePressed,
                ]}
              >
                <Text style={styles.dropsetStepRemoveText}>×</Text>
              </Pressable>
            ) : (
              <View style={styles.dropsetStepRemove} />
            )}
          </View>
        );
      })}
    </View>
  );
}
